// ═══ EvalInspector — 콜 상세 + 검차 입력 ═══
// EvalInspectorPage.js에서 분리. order/inspector를 받아 콜 잡기/검차 시작/완료를 처리.
import React, { useState, useMemo } from 'react';
import DamageCheckMap from './DamageCheckMap';
import PaintOCR from './PaintOCR';
import InspectionPhotos from './InspectionPhotos';
import CustomModal from './CustomModal';
import { Badge, Stars, getPaintColor } from './EvalInspectorShared';
import useEvalThumbBlobs from '../utils/useEvalThumbBlobs';

function CallDetail({ order, inspector, onBack, onTake, onStart, onComplete }) {
  var [modal, setModal] = useState(null);
  var closeModal = function() { setModal(null); };
  const [evalForm, setEvalForm] = useState({ exterior: 0, interior: 0, engine: 0, undercarriage: 0, overall: 0 });
  const [paintForm, setPaintForm] = useState({ hood: null, roof: null, fenderL: null, fenderR: null, doorFL: null, doorFR: null, doorRL: null, doorRR: null, trunk: null, quarterL: null, quarterR: null });
  const [damageData, setDamageData] = useState({});
  const [inspPhotos, setInspPhotos] = useState({});
  const [memo, setMemo] = useState('');

  const isMyCall = order.inspector_id === inspector.id;
  const evalLabels = ['외관', '내관', '엔진', '하부', '종합'];
  const evalKeys = ['exterior', 'interior', 'engine', 'undercarriage', 'overall'];

  // 🔴 SECURITY (Wave 1B, 2026-04-29): 검차 사진 thumb 인증 다운로드 (blob URL Map)
  // 정적 URL (/uploads/eval/*) 직접 참조 제거 — 인증 endpoint /api/eval/orders/<id>/photos/<filename>/thumb 사용.
  // useMemo 로 photos 안정화 (eval_result 변경 없이 부모 리렌더 시 hook 재실행 방지).
  const completedPhotos = useMemo(function() {
    if (!order.eval_result) return [];
    var p = order.eval_result.photos;
    if (typeof p === 'string') { try { p = JSON.parse(p); } catch (e) { p = []; } }
    if (!Array.isArray(p)) return [];
    return p.filter(function(x) { return x && x.url && !x.url.startsWith('blob:'); });
  }, [order.eval_result]);
  const completedThumbBlobs = useEvalThumbBlobs(completedPhotos);

  const handleComplete = () => {
    if (!evalForm.overall) { setModal({ type: 'danger', title: '실패', message: '종합 평가를 선택해주세요', hideCancel: true, onConfirm: closeModal }); return; }
    // Wave W3 (2026-05-21) — 검차 제출 전 확인 다이얼로그. 실수 클릭 차단.
    setModal({
      type: 'confirm',
      title: '검차 제출',
      message: '입력한 도막/사고/평가/사진을 모두 제출합니다. 제출 후 수정은 관리자만 가능합니다.\n계속하시겠습니까?',
      confirmText: '제출',
      onConfirm: function() {
        closeModal();
        doSubmitComplete();
      },
    });
  };
  const doSubmitComplete = () => {
    // DB 컬럼명(snake_case)으로 변환해서 전달
    var completeData = {
      paint_hood: paintForm.hood ? parseInt(paintForm.hood) : null,
      paint_roof: paintForm.roof ? parseInt(paintForm.roof) : null,
      paint_fender_l: paintForm.fenderL ? parseInt(paintForm.fenderL) : null,
      paint_fender_r: paintForm.fenderR ? parseInt(paintForm.fenderR) : null,
      paint_door_fl: paintForm.doorFL ? parseInt(paintForm.doorFL) : null,
      paint_door_fr: paintForm.doorFR ? parseInt(paintForm.doorFR) : null,
      paint_door_rl: paintForm.doorRL ? parseInt(paintForm.doorRL) : null,
      paint_door_rr: paintForm.doorRR ? parseInt(paintForm.doorRR) : null,
      paint_trunk: paintForm.trunk ? parseInt(paintForm.trunk) : null,
      paint_quarter_l: paintForm.quarterL ? parseInt(paintForm.quarterL) : null,
      paint_quarter_r: paintForm.quarterR ? parseInt(paintForm.quarterR) : null,
      score_exterior: evalForm.exterior || 0,
      score_interior: evalForm.interior || 0,
      score_engine: evalForm.engine || 0,
      score_undercarriage: evalForm.undercarriage || 0,
      score_overall: evalForm.overall || 0,
      damage_data: damageData || {},
      inspector_notes: memo || order.notes || '',
      photos: inspPhotos,
    };
    onComplete(order.id, completeData);
  };

  const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #eee', padding: 16, marginBottom: 10 };
  const paintFields = [['본넷', 'hood'], ['루프', 'roof'], ['좌펜더', 'fenderL'], ['우펜더', 'fenderR'], ['좌앞문', 'doorFL'], ['우앞문', 'doorFR'], ['좌뒷문', 'doorRL'], ['우뒷문', 'doorRR'], ['트렁크', 'trunk'], ['좌뒤펜더', 'quarterL'], ['우뒤펜더', 'quarterR']];

  return (
    <div style={{ padding: '12px 16px', paddingBottom: 100 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 15, color: '#3B8BD4', padding: 0, marginBottom: 12, fontWeight: 600, cursor: 'pointer' }}>← 뒤로</button>

      {/* 차량 정보 */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>{order.car_model}</span>
          <Badge status={order.status} />
        </div>
        <div style={{ fontSize: 13, color: '#888' }}>{order.car_detail} · {order.car_number || '번호미등록'}</div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>{order.car_year}년 · {(order.car_mileage || 0).toLocaleString()}km · {order.car_color}</div>
      </div>

      {/* 위치 + 일정 */}
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#222', marginBottom: 4 }}>{order.location}</div>
        <div style={{ fontSize: 13, color: '#888' }}>{order.location_detail}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 13 }}>
          <span style={{ color: '#888' }}>{order.inspect_date} {order.inspect_time}</span>
          <span style={{ color: '#1D9E75', fontWeight: 700 }}>{(order.fee || 0).toLocaleString()}원</span>
        </div>
        {order.seller_phone && (
          <a href={`tel:${order.seller_phone}`} style={{
            display: 'block', marginTop: 10, padding: '10px 0', borderRadius: 8,
            background: '#E6F1FB', color: '#185FA5', textAlign: 'center',
            fontSize: 14, fontWeight: 600, textDecoration: 'none',
          }}>판매자 전화 {order.seller_phone}</a>
        )}
      </div>

      {/* 길찾기 (내비 앱 → 웹 폴백) */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <a href={'tmap://route?goalname=' + encodeURIComponent((order.location || '') + ' ' + (order.location_detail || '')) + '&goalx=' + (order.location_lng || '') + '&goaly=' + (order.location_lat || '')}
          onClick={function() { setTimeout(function() { var isIOS = /iPhone|iPad/i.test(navigator.userAgent); window.location.href = isIOS ? 'https://apps.apple.com/kr/app/tmap/id431589174' : 'https://play.google.com/store/apps/details?id=com.skt.tmap.ku'; }, 1500); }}
          style={{ flex: 1, padding: '12px 0', borderRadius: 10, background: '#EF4444', color: '#fff', textAlign: 'center', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>T map</a>
        <a href={'kakaonavi-sdk://navigate?ep=' + encodeURIComponent((order.location || '') + ' ' + (order.location_detail || '')) + '&by=CAR'}
          onClick={function() { setTimeout(function() { var isIOS = /iPhone|iPad/i.test(navigator.userAgent); window.location.href = isIOS ? 'https://apps.apple.com/kr/app/kakaonavi/id417698849' : 'https://play.google.com/store/apps/details?id=com.locnall.KimGiSa'; }, 1500); }}
          style={{ flex: 1, padding: '12px 0', borderRadius: 10, background: '#FEE500', color: '#3C1E1E', textAlign: 'center', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>카카오내비</a>
      </div>

      {order.notes && (
        <div style={{ ...cardStyle, background: '#FAEEDA', border: '1px solid #FAC775' }}>
          <div style={{ fontSize: 12, color: '#854F0B', fontWeight: 500, marginBottom: 4 }}>메모</div>
          <div style={{ fontSize: 13, color: '#633806', lineHeight: 1.5 }}>{order.notes}</div>
        </div>
      )}

      {/* ── 콜 잡기 ── */}
      {order.status === 'open' && (
        <button onClick={() => onTake(order.id)} style={{
          width: '100%', padding: '16px 0', borderRadius: 14, border: 'none',
          background: '#3B8BD4', color: '#fff', fontSize: 18, fontWeight: 800, cursor: 'pointer',
          letterSpacing: '-0.5px',
        }}>이 콜 잡기</button>
      )}

      {/* ── 검차 시작 ── */}
      {order.status === 'taken' && isMyCall && (
        <button onClick={() => onStart(order.id)} style={{
          width: '100%', padding: '16px 0', borderRadius: 14, border: 'none',
          background: '#BA7517', color: '#fff', fontSize: 18, fontWeight: 800, cursor: 'pointer',
        }}>검차 시작</button>
      )}

      {/* ── 검차 입력 폼 ── */}
      {order.status === 'inspecting' && isMyCall && (
        <div style={{ ...cardStyle, border: '2px solid #534AB7' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#222', marginBottom: 14 }}>평가 입력</div>

          {/* 사고 부위 체크 + 사진 */}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#534AB7', marginBottom: 8 }}>사고 부위 체크</div>
          <DamageCheckMap value={damageData} onChange={setDamageData} onDamagePhoto={function(files, memo) {
            var newPhotos = files.map(function(file) {
              return { id: Date.now() + Math.random(), file: file, preview: URL.createObjectURL(file), memo: memo, timestamp: new Date().toLocaleTimeString() };
            });
            setInspPhotos(function(prev) {
              var damageList = (prev.damage || []).concat(newPhotos);
              return Object.assign({}, prev, { damage: damageList });
            });
          }} />
          <div style={{ height: 16 }} />

          {/* 검차 사진 */}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#534AB7', marginBottom: 8, marginTop: 16 }}>검차 사진</div>
          <InspectionPhotos photos={inspPhotos} onChange={setInspPhotos} />
          <div style={{ height: 16 }} />

          {/* 도막 사진 OCR */}
          <PaintOCR paintData={paintForm} onUpdate={function(key, val) { setPaintForm(function(p) { var n = {}; n[key] = val; return Object.assign({}, p, n); }); }} />
          <div style={{ height: 16 }} />

          {/* 도막 수동 입력 */}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#534AB7', marginBottom: 8 }}>도막 수동 입력 (μm)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 16 }}>
            {paintFields.map(([label, key]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#888', width: 44, flexShrink: 0 }}>{label}</span>
                <input type="number" inputMode="numeric" value={paintForm[key]}
                  onChange={e => setPaintForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder="μm"
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                    border: `1.5px solid ${paintForm[key] ? getPaintColor(+paintForm[key]) : '#e0e0da'}`,
                    borderRadius: 8, fontSize: 14, outline: 'none',
                    background: paintForm[key] ? `${getPaintColor(+paintForm[key])}11` : '#fff',
                  }} />
              </div>
            ))}
          </div>

          {/* 상태 평가 */}
          <div style={{ fontSize: 13, fontWeight: 600, color: '#534AB7', marginBottom: 8 }}>상태 평가</div>
          {evalKeys.map((key, i) => (
            <div key={key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 0', borderTop: key === 'overall' ? '1px solid #eee' : 'none',
              marginTop: key === 'overall' ? 6 : 0,
            }}>
              <span style={{ fontSize: 14, color: key === 'overall' ? '#222' : '#666', fontWeight: key === 'overall' ? 700 : 400 }}>{evalLabels[i]}</span>
              <Stars value={evalForm[key]} size={key === 'overall' ? 24 : 20} onChange={v => setEvalForm(p => ({ ...p, [key]: v }))} />
            </div>
          ))}

          {/* 메모 */}
          <textarea value={memo} onChange={e => setMemo(e.target.value)} rows={3}
            placeholder="특이사항, 사고이력, 소음, 누유 등 상세하게 작성..."
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px',
              border: '1.5px solid #e0e0da', borderRadius: 10, fontSize: 14,
              outline: 'none', marginTop: 14, marginBottom: 14, resize: 'vertical',
              lineHeight: 1.5,
            }} />

          {/* 도막 범례 */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, justifyContent: 'center' }}>
            {[['#1D9E75', '~150 정상'], ['#EF9F27', '150~250 도장'], ['#E24B4A', '250+ 판금']].map(([c, l]) => (
              <span key={l} style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: c }} /><span style={{ color: '#888' }}>{l}</span>
              </span>
            ))}
          </div>

          <button onClick={handleComplete} style={{
            width: '100%', padding: '16px 0', borderRadius: 14, border: 'none',
            background: evalForm.overall > 0 ? '#534AB7' : '#ccc',
            color: '#fff', fontSize: 18, fontWeight: 800,
            cursor: evalForm.overall > 0 ? 'pointer' : 'default',
          }}>검차 완료 제출</button>
        </div>
      )}

      {/* ── 검차 완료 결과 ── */}
      {(order.status === 'completed' || order.status === 'decided' || order.status === 'rejected') && order.eval_result && (function() {
        var er = order.eval_result;
        var dmg = er.damage_data;
        if (dmg && typeof dmg === 'string') { try { dmg = JSON.parse(dmg); } catch(e) { dmg = {}; } }
        // photos 는 outer useMemo (completedPhotos) 사용 — Wave 1B blob URL 마이그
        var photos = completedPhotos;
        return (
        <div>
          {/* 별점 평가 */}
          <div style={cardStyle}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#222', marginBottom: 10 }}>평가 결과</div>
            {[['외관', er.score_exterior], ['내관', er.score_interior], ['엔진', er.score_engine], ['하부', er.score_undercarriage], ['종합', er.score_overall]].map(function(pair) {
              return (
                <div key={pair[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: pair[0] === '종합' ? 'none' : '1px solid #f0f0ec' }}>
                  <span style={{ fontSize: 13, color: pair[0] === '종합' ? '#222' : '#888', fontWeight: pair[0] === '종합' ? 600 : 400 }}>{pair[0]}</span>
                  <Stars value={pair[1] || 0} size={14} />
                </div>
              );
            })}
          </div>

          {/* 도막 수치 */}
          {(er.paint_hood || er.paint_roof || er.paint_fender_l) && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#222', marginBottom: 8 }}>도막 측정</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {[['본넷', er.paint_hood], ['루프', er.paint_roof], ['좌펜더', er.paint_fender_l], ['우펜더', er.paint_fender_r], ['좌앞문', er.paint_door_fl], ['우앞문', er.paint_door_fr], ['좌뒷문', er.paint_door_rl], ['우뒷문', er.paint_door_rr]].map(function(pair) {
                  var color = getPaintColor(pair[1]);
                  return (
                    <div key={pair[0]} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', background: pair[1] ? (pair[1] < 150 ? '#E1F5EE' : pair[1] < 250 ? '#FAEEDA' : '#FCEBEB') : '#f8f8f5', borderRadius: 6 }}>
                      <span style={{ fontSize: 12, color: '#888' }}>{pair[0]}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: color }}>{pair[1] || '-'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 사고부위 */}
          {dmg && Object.keys(dmg).length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#222', marginBottom: 8 }}>사고 부위</div>
              <DamageCheckMap value={dmg} readOnly={true} />
            </div>
          )}

          {/* 검차 사진 */}
          {photos.length > 0 && (
            <div style={cardStyle}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#222', marginBottom: 8 }}>검차 사진 ({photos.length}장)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {photos.map(function(p, i) {
                  return (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={completedThumbBlobs[p.url] || ''} alt={p.memo || ''} loading="lazy" decoding="async" width="400" height="400" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', background: '#f0f0ec' }} />
                      {p.memo && <div style={{ position: 'absolute', bottom: 3, left: 3, right: 3, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 9, padding: '2px 6px', borderRadius: 4, textAlign: 'center' }}>{p.memo}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 관리자 리뷰 */}
          {order.inspector_review && (
            <div style={{ ...cardStyle, background: '#FAEEDA', border: '1px solid #FAC775' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#854F0B', marginBottom: 4 }}>관리자 리뷰</div>
              <Stars value={order.inspector_review.score_overall || order.inspector_review.overall || 0} size={14} />
              {(order.inspector_review.comment) && <div style={{ fontSize: 12, color: '#633806', marginTop: 4 }}>{order.inspector_review.comment}</div>}
              <div style={{ fontSize: 10, color: '#888', marginTop: 4, display: 'flex', gap: 8 }}>
                <span>신속 {order.inspector_review.score_speed || 0}★</span>
                <span>정확 {order.inspector_review.score_accuracy || 0}★</span>
                <span>꼼꼼 {order.inspector_review.score_detail || 0}★</span>
              </div>
            </div>
          )}
        </div>
        );
      })()}
      {modal && <CustomModal isOpen={true} {...modal} onCancel={closeModal} />}
    </div>
  );
}

export default CallDetail;
